import { useState } from 'react';
import { Banner, Button, Field, Input, KeyValue, Pill } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { fmtDate, todayISO } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';

/**
 * Claims of Lien are recorded at the county clerk by hand today. This tab is
 * the checklist plus the place to record the book/page once it comes back, so
 * the enforcement clock starts from the real recording date.
 */
export default function FilingTab({ job, reload, goToTab }: TabProps) {
  const { can } = useSession();
  const mayFile = can('manager');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Blank on purpose: a pre-filled "today" made it one stray click to log a
  // lien that was never recorded.
  const [draft, setDraft] = useState({ bookPage: '', filedDate: '' });
  const [confirming, setConfirming] = useState(false);

  const checklist = job.filingChecklist;

  const review = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (draft.filedDate > todayISO()) {
      setError('The recording date can’t be in the future.');
      return;
    }
    setConfirming(true);
  };

  const record = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      if (mayFile) {
        await api.post(`/api/jobs/${job.id}/lien-filed`, draft);
        setDone('Recording details saved. The one-year enforcement deadline now runs from this date.');
      } else {
        await api.post(`/api/jobs/${job.id}/approval-requests`, { actionType: 'lien_file', payload: draft });
        setDone('Sent to the office for approval.');
      }
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the recording details');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-ink">County clerk</h3>
        <KeyValue
          rows={[
            ['Clerk', checklist?.clerkName],
            ['Recording address', checklist?.recordingAddress],
            ['Website', checklist?.website ? <a className="text-tint hover:underline" href={checklist.website} target="_blank" rel="noreferrer">{checklist.website}</a> : null],
            ['Expected fee', checklist?.expectedFee],
          ]}
        />

        <h3 className="pt-2 text-sm font-semibold text-ink">Claim of Lien checklist</h3>
        <ol className="space-y-2">
          {(checklist?.steps ?? []).map((step, i) => (
            <li key={i} className="flex gap-3 rounded-lg border border-border bg-surface-alt/40 px-3 py-2 text-sm text-muted">
              <span className="font-mono text-xs text-dim">{String(i + 1).padStart(2, '0')}</span>
              <span>{step}</span>
            </li>
          ))}
          {!checklist?.steps?.length && <li className="text-sm text-dim">No checklist for this county yet.</li>}
        </ol>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-ink">Recording</h3>
        {job.lienFiled ? (
          <Banner tone="ok" title="Recording details saved">
            You reported the Claim of Lien recorded {fmtDate(job.lienFiledAt)}, Book/Page {job.lienBookPage}. Enforcement
            deadline {fmtDate(job.lienEnforcementDeadline)} (F.S. 713.22).
          </Banner>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Pill tone="watch">Not recorded</Pill>
              {job.lienDeadline ? (
                <span className="text-xs text-muted">Lien deadline {fmtDate(job.lienDeadline)}</span>
              ) : (
                <span className="text-xs text-muted">
                  Log your last day of work to see your lien deadline.{' '}
                  {goToTab && (
                    <button type="button" className="font-semibold text-tint hover:underline" onClick={() => goToTab('work')}>
                      Work Log →
                    </button>
                  )}
                </span>
              )}
            </div>
            <p className="text-sm text-muted">
              Record the Claim of Lien with the county clerk first. Then enter what the clerk stamped here — LienGuardian
              saves it to this job and tracks your one-year enforcement deadline. Nothing is filed from here.
            </p>
            {!mayFile && (
              <Banner tone="watch" title="Your seat cannot file a lien">
                Submitting here sends the recording details to the office to approve.
              </Banner>
            )}
            {error && <Banner tone="urgent">{error}</Banner>}
            {done && <Banner tone="ok">{done}</Banner>}
            <form onSubmit={review} className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
              <Field label="Book / page" hint="As stamped by the clerk, e.g. 34512/1180.">
                <Input required value={draft.bookPage} onChange={(e) => { setConfirming(false); setDraft({ ...draft, bookPage: e.target.value }); }} />
              </Field>
              <Field label="Date the clerk recorded it">
                <Input
                  required
                  type="date"
                  max={todayISO()}
                  value={draft.filedDate}
                  onChange={(e) => { setConfirming(false); setDraft({ ...draft, filedDate: e.target.value }); }}
                />
              </Field>
              {confirming ? (
                <div className="space-y-3 rounded-lg border border-watch/40 bg-watch/10 p-3 text-sm text-ink">
                  <p>
                    Confirm: the clerk recorded your Claim of Lien on <strong>{fmtDate(draft.filedDate)}</strong> at Book/Page{' '}
                    <strong>{draft.bookPage.trim()}</strong>. Your one-year enforcement deadline will run from that date.
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => void record()} disabled={busy}>
                      {mayFile ? 'Yes, save it' : 'Yes, send for approval'}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                      Go back
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="submit" disabled={busy} className="w-full">
                  {busy ? 'Saving…' : mayFile ? 'Save recording details' : 'Send to office for approval'}
                </Button>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
